import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Slider,
  Typography,
  Box
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';

function VitalsDialog({ open, onClose, visitId, existingVitals, onSuccess, onError }) {
  const [vitals, setVitals] = useState({
    bloodPressure: '',
    temperature: '',
    pulseRate: '',
    respiratoryRate: '',
    weight: '',
    height: '',
    bmi: '',
    oxygenSaturation: '',
    painScale: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existingVitals) {
      setVitals({
        bloodPressure: existingVitals.blood_pressure || '',
        temperature: existingVitals.temperature || '',
        pulseRate: existingVitals.pulse_rate || '',
        respiratoryRate: existingVitals.respiratory_rate || '',
        weight: existingVitals.weight || '',
        height: existingVitals.height || '',
        bmi: existingVitals.bmi || '',
        oxygenSaturation: existingVitals.oxygen_saturation || '',
        painScale: existingVitals.pain_scale || ''
      });
    }
  }, [existingVitals]);

  // Auto-calculate BMI when weight or height changes
  useEffect(() => {
    if (vitals.weight && vitals.height) {
      const weightKg = parseFloat(vitals.weight);
      const heightM = parseFloat(vitals.height) / 100;
      if (weightKg > 0 && heightM > 0) {
        const bmiValue = (weightKg / (heightM * heightM)).toFixed(2);
        setVitals(prev => ({ ...prev, bmi: bmiValue }));
      }
    }
  }, [vitals.weight, vitals.height]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSuccess(vitals);
      handleClose();
    } catch (error) {
      if (onError) {
        onError(error.response?.data?.message || 'Failed to save vitals');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {existingVitals ? 'Update Vitals' : 'Add Vitals'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Blood Pressure"
              placeholder="120/80"
              value={vitals.bloodPressure}
              onChange={(e) => setVitals({ ...vitals, bloodPressure: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Temperature (°F)"
              value={vitals.temperature}
              onChange={(e) => setVitals({ ...vitals, temperature: e.target.value })}
              inputProps={{ step: "0.1", min: "90", max: "110" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Pulse Rate (bpm)"
              value={vitals.pulseRate}
              onChange={(e) => setVitals({ ...vitals, pulseRate: e.target.value })}
              inputProps={{ min: "30", max: "200" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Respiratory Rate"
              value={vitals.respiratoryRate}
              onChange={(e) => setVitals({ ...vitals, respiratoryRate: e.target.value })}
              inputProps={{ min: "5", max: "60" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Weight (kg)"
              value={vitals.weight}
              onChange={(e) => setVitals({ ...vitals, weight: e.target.value })}
              inputProps={{ step: "0.1", min: "1", max: "500" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Height (cm)"
              value={vitals.height}
              onChange={(e) => setVitals({ ...vitals, height: e.target.value })}
              inputProps={{ step: "0.1", min: "30", max: "250" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="BMI"
              value={vitals.bmi}
              onChange={(e) => setVitals({ ...vitals, bmi: e.target.value })}
              inputProps={{ step: "0.01", min: "10", max: "100" }}
              helperText="Auto-calculated from weight and height"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Oxygen Saturation (%)"
              value={vitals.oxygenSaturation}
              onChange={(e) => setVitals({ ...vitals, oxygenSaturation: e.target.value })}
              inputProps={{ min: "50", max: "100" }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Pain Scale (0-10 Numerical Rating Scale)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  value={vitals.painScale ? parseInt(vitals.painScale) : 0}
                  onChange={(_, value) => setVitals({ ...vitals, painScale: value })}
                  min={0}
                  max={10}
                  step={1}
                  marks
                  valueLabelDisplay="on"
                  sx={{ flexGrow: 1 }}
                />
                <TextField
                  type="number"
                  value={vitals.painScale}
                  onChange={(e) => setVitals({ ...vitals, painScale: e.target.value })}
                  inputProps={{ min: "0", max: "10", step: "1" }}
                  sx={{ width: 80 }}
                  size="small"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography variant="caption" color="text.secondary">😊 0 - No Pain</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {vitals.painScale !== '' && vitals.painScale !== null && (
                    <>
                      {parseInt(vitals.painScale) === 0 && '😊 No Pain'}
                      {parseInt(vitals.painScale) >= 1 && parseInt(vitals.painScale) <= 3 && '😐 Mild Pain'}
                      {parseInt(vitals.painScale) >= 4 && parseInt(vitals.painScale) <= 6 && '😣 Moderate Pain'}
                      {parseInt(vitals.painScale) >= 7 && parseInt(vitals.painScale) <= 9 && '😖 Severe Pain'}
                      {parseInt(vitals.painScale) === 10 && '😱 Worst Pain'}
                    </>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">😱 10 - Worst Pain</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} startIcon={<Cancel />} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default VitalsDialog;